import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { closeLive } from '../lib/live';
import type { Me } from '../types';

// Warum die Sitzung endete, wenn nicht selbst abgemeldet: „idle“ = automatisch nach längerer Zeit ohne Eingabe
export type EndReason = 'idle' | null;

interface SessionValue {
  me: Me | null;
  loading: boolean;
  // Selbst abgemeldet („Abmelden“): Die Anmeldeseite merkt sich dann keine Seite, wer sich als Nächstes
  // anmeldet, landet auf seiner eigenen Startseite. Bei abgelaufener Sitzung bleibt die Seite gemerkt.
  signedOut: boolean;
  ended: EndReason;
  refresh(): Promise<Me | null>;
  logout(): Promise<void>;
  // Sitzung ist auf dem Server schon vorbei: zur Anmeldung, die aufgerufene Seite bleibt gemerkt
  expire(reason: EndReason): void;
}

const SessionContext = createContext<SessionValue | null>(null);

// Ungespeicherte Buchungsentwürfe der Seite „Buchen“ (sessionStorage) beim Abmelden nicht im Tab lassen
function entwuerfeLoeschen() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('buchen-entwurf:')) sessionStorage.removeItem(k);
    }
  } catch {
    // ohne Speicher nichts zu tun
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedOut, setSignedOut] = useState(false);
  const [ended, setEnded] = useState<EndReason>(null);

  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>('/api/auth/me');
      setMe(m);
      setSignedOut(false);
      setEnded(null);
      return m;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (err.code === 'idle') setEnded('idle');
        setMe(null);
        return null;
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => {});
    entwuerfeLoeschen();
    closeLive();
    setSignedOut(true);
    setEnded(null);
    setMe(null);
  }, []);

  const expire = useCallback((reason: EndReason) => {
    entwuerfeLoeschen();
    closeLive();
    setSignedOut(false);
    setEnded(reason);
    setMe(null);
  }, []);

  useEffect(() => {
    refresh().catch(() => setLoading(false));
    // Sitzung abgelaufen oder anderswo beendet (Code im Ereignis, z. B. „idle“)
    const onLoggedOut = (e: Event) => expire((e as CustomEvent<string | undefined>).detail === 'idle' ? 'idle' : null);
    window.addEventListener('nt:logged-out', onLoggedOut);
    return () => window.removeEventListener('nt:logged-out', onLoggedOut);
  }, [refresh, expire]);

  return (
    <SessionContext.Provider value={{ me, loading, signedOut, ended, refresh, logout, expire }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession außerhalb von SessionProvider');
  return v;
}
